import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, User, Trash2, CheckCircle2, Inbox, Archive, RotateCcw } from "lucide-react";

const STATUS_LABEL = {
  new: "Nova",
  reviewed: "Analisada",
  archived: "Arquivada",
};

const STATUS_STYLE = {
  new: "bg-[#C6A762]/10 text-[#C6A762] border-[#C6A762]/30",
  reviewed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  archived: "bg-white/5 text-[#7d7d7d] border-[#2a2a2a]",
};

export default function RouteSuggestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | reviewed | archived

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/route-suggestions")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Falha ao carregar sugestões"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/route-suggestions/${id}/status`, null, { params: { status } });
      setItems((arr) => arr.map((s) => (s.id === id ? { ...s, status } : s)));
      toast.success(`Marcado como "${STATUS_LABEL[status]}"`);
    } catch { toast.error("Falha ao atualizar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta sugestão? Esta ação é irreversível.")) return;
    try {
      await adminApi.delete(`/admin/route-suggestions/${id}`);
      setItems((arr) => arr.filter((s) => s.id !== id));
      toast.success("Sugestão excluída");
    } catch { toast.error("Falha ao excluir"); }
  };

  const filtered = filter === "all" ? items : items.filter((s) => (s.status || "new") === filter);
  const counts = {
    all: items.length,
    new: items.filter((s) => (s.status || "new") === "new").length,
    reviewed: items.filter((s) => s.status === "reviewed").length,
    archived: items.filter((s) => s.status === "archived").length,
  };

  return (
    <div className="space-y-8" data-testid="admin-route-suggestions">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 text-[#C6A762] mb-3">
            <span className="gold-divider" />
            <span className="text-[11px] tracking-[0.35em] uppercase">Comunidade</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl">Sugestões de Rotas</h1>
          <p className="text-[#B8B8B8] mt-2 text-sm">
            {counts.new} nova(s) · {counts.reviewed} analisada(s) · {counts.archived} arquivada(s)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: "Todas" },
          { k: "new", label: "Novas" },
          { k: "reviewed", label: "Analisadas" },
          { k: "archived", label: "Arquivadas" },
        ].map((tab) => (
          <button
            key={tab.k}
            onClick={() => setFilter(tab.k)}
            data-testid={`filter-${tab.k}`}
            className={`px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase transition-colors border ${
              filter === tab.k
                ? "border-[#C6A762] text-[#C6A762] bg-[#C6A762]/10"
                : "border-[#242424] text-[#B8B8B8] hover:text-white"
            }`}
          >
            {tab.label} <span className="opacity-60 ml-1">{counts[tab.k]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#7d7d7d]"><Loader2 className="inline animate-spin" size={18} /></div>
      ) : filtered.length === 0 ? (
        <div className="card-rotta p-12 text-center">
          <Inbox size={32} className="mx-auto text-[#C6A762] opacity-60" />
          <p className="text-[#B8B8B8] mt-4 text-sm">Nenhuma sugestão nesta categoria.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((s) => {
            const status = s.status || "new";
            return (
              <div key={s.id} className="card-rotta p-6" data-testid={`suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-xl text-white">{s.route_name}</h3>
                      <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border ${STATUS_STYLE[status]}`}>
                        {STATUS_LABEL[status]}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#B8B8B8]">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={12} className="text-[#C6A762]" /> {s.city_state}
                      </span>
                      {s.name && (
                        <span className="inline-flex items-center gap-1.5">
                          <User size={12} className="text-[#C6A762]" /> {s.name}
                        </span>
                      )}
                      {s.email && (
                        <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-[#C6A762]">
                          <Mail size={12} className="text-[#C6A762]" /> {s.email}
                        </a>
                      )}
                      <span className="text-[#7d7d7d]">
                        {s.created_at ? new Date(s.created_at).toLocaleString("pt-BR") : ""}
                      </span>
                    </div>
                    <p className="text-sm text-white/85 mt-4 leading-relaxed whitespace-pre-line">
                      {s.description}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    {status !== "reviewed" && (
                      <button
                        onClick={() => setStatus(s.id, "reviewed")}
                        data-testid={`mark-reviewed-${s.id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10"
                      >
                        <CheckCircle2 size={12} /> Analisada
                      </button>
                    )}
                    {status !== "archived" ? (
                      <button
                        onClick={() => setStatus(s.id, "archived")}
                        data-testid={`archive-${s.id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase text-[#B8B8B8] border border-[#2a2a2a] hover:text-white"
                      >
                        <Archive size={12} /> Arquivar
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(s.id, "new")}
                        data-testid={`reopen-${s.id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase text-[#C6A762] border border-[#C6A762]/30 hover:bg-[#C6A762]/10"
                      >
                        <RotateCcw size={12} /> Reabrir
                      </button>
                    )}
                    <button
                      onClick={() => remove(s.id)}
                      data-testid={`delete-${s.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase text-red-400 border border-red-500/20 hover:bg-red-500/10"
                    >
                      <Trash2 size={12} /> Excluir
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
